(() => {
  'use strict';

  // ============================================================================
  // Image Task Module
  // ============================================================================
  // Handles image task creation, validation, and updates
  // ============================================================================

  const ImageTaskModule = (() => {
    // ============================================================================
    // Constants
    // ============================================================================
    const STATUS = {
      PENDING: 'pending',
      SUCCESS: 'success',
      ERROR: 'error',
      SKIP: 'skip'
    };

    // ============================================================================
    // Task Creation
    // ============================================================================

    /**
     * Generate original file ID (no color suffix)
     * @param {string} name - File name
     * @param {number} size - File size
     * @param {number} lastModified - File last modified timestamp
     * @returns {string|null} Original file ID or null if invalid
     */
    const generateOriginalFileId = (name, size, lastModified) => {
      if (!name) return null;
      if (typeof size !== 'number' || typeof lastModified !== 'number') return null;
      return `${name}_${size}_${lastModified}`;
    };

    /**
     * Generate task ID from original file ID and color
     * @param {string} originalFileId - Original file ID
     * @param {string} color - Color value
     * @returns {string} Task ID
     */
    const generateTaskId = (originalFileId, color) => {
      if (originalFileId && color) {
        return `${originalFileId}_${color}`;
      }
      return `task-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    /**
     * Extract original file ID from task ID (removes color suffix)
     * @param {string} taskId - Task ID (format: originalFileId_color)
     * @param {string} color - Current color
     * @returns {string|null} Original file ID or null if invalid
     */
    const extractOriginalFileIdFromTaskId = (taskId, color) => {
      if (!taskId || !color) return null;
      const suffix = `_${color}`;
      return taskId.endsWith(suffix) ? taskId.slice(0, -suffix.length) : null;
    };

    /**
     * Create a new image task object
     * @param {string|null} originalFileId - Original file ID (without color)
     * @param {string|null} color - Color value
     * @param {string|null} historyId - History ID if loading from history
     * @returns {Object} Image task object
     */
    const create = (originalFileId, color, historyId = null) => {
      const id = generateTaskId(originalFileId, color);

      return {
        id,
        fileOriginalId: originalFileId || null,
        color: color || null,
        historyId: historyId || null,
        // These will be populated during processing
        fileUrl: null,
        s3Url: null,
        pillowData: null,
        preview: null,
        dataUrl: null,
        fileType: null,
        fileName: null,
        extension: null,
        status: {
          pillow3d: STATUS.PENDING,
          saveToS3: STATUS.PENDING
        },
        error: {
          event: null,
          type: null
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    };

    /**
     * Create an empty task structure (for initialization)
     * @returns {Object} Empty task structure
     */
    const createEmpty = () => {
      return {
        id: null,
        fileOriginalId: null,
        color: null,
        historyId: null,
        fileUrl: null,
        s3Url: null,
        pillowData: null,
        preview: null,
        dataUrl: null,
        fileType: null,
        fileName: null,
        extension: null,
        status: {
          pillow3d: STATUS.PENDING,
          saveToS3: STATUS.PENDING
        },
        error: {
          event: null,
          type: null
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    };

    // ============================================================================
    // Task Updates
    // ============================================================================

    /**
     * Update task with new values
     * If color is updated, automatically updates id and clears historyId
     * @param {Object} task - Current task object
     * @param {Object} updates - Updates to apply
     * @param {string|null} taskId - Optional task ID to validate against
     * @returns {Object|null} Updated task or null if validation failed
     */
    const update = (task, updates = {}, taskId = null) => {
      if (!task) return null;

      // If taskId provided, verify it matches current task
      if (taskId && task.id !== taskId) {
        return null; // ID doesn't match, don't apply updates
      }

      // If color is being updated, automatically update id and clear historyId
      if (updates.color !== undefined) {
        if (task.fileOriginalId) {
          updates.id = generateTaskId(task.fileOriginalId, updates.color);
        }
        updates.historyId = null;
      }

      // Apply updates
      const updatedTask = {
        ...task,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      return updatedTask;
    };

    /**
     * Update task for color change - clears pillowData and preview, updates color and id
     * @param {Object} task - Current task object
     * @param {string} newColor - New color value
     * @returns {Object|null} Updated task or null if invalid
     */
    const updateForColorChange = (task, newColor) => {
      if (!newColor || !task) {
        return null;
      }

      return update(task, {
        color: newColor,
        pillowData: null,
        preview: null
      }, task.id);
    };

    // ============================================================================
    // Task Validation
    // ============================================================================

    /**
     * Validate that taskId matches the task
     * @param {Object} task - Task object
     * @param {string} taskId - Task ID to validate
     * @returns {boolean} True if taskId matches task.id
     */
    const validateTaskId = (task, taskId) => {
      if (!task || !taskId) return false;
      return task.id === taskId;
    };


    /**
     * Generate original file ID from file object
     * @param {File} file - File object
     * @returns {string|null} Original file ID or null
     */
    const generateFileOriginalId = (file) => {
      if (file?.name && typeof file.size === 'number' && typeof file.lastModified === 'number') {
        return generateOriginalFileId(file.name, file.size, file.lastModified);
      }
      return null;
    };

    // ============================================================================
    // Public API
    // ============================================================================
    return {
      // Creation
      create,
      createEmpty,
      
      // Updates
      update,
      updateForColorChange,
      
      // Validation
      validateTaskId,
      
      // ID Generation
      generateOriginalFileId,
      generateTaskId,
      extractOriginalFileIdFromTaskId,
      generateFileOriginalId,
      
      // Constants
      STATUS
    };
  })();

  // Export to global scope
  if (typeof window !== 'undefined') {
    window.ImageTaskModule = ImageTaskModule;
  }

})();

